// Spotfire to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const spotfirePage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Spotfire to Amazon Quick',
    intro: 'The reasons we hear most from Spotfire customers running on AWS.',
    cards: [
      { title: 'A lighter platform to run', description: 'No Spotfire Server, Web Player, or node manager fleet to patch and scale. Quick is fully managed on AWS.' },
      { title: 'Data science on managed services', description: 'Python, R, and TERR data functions move to AWS Lambda or SageMaker, where they can be versioned and scaled.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Spotfire metadata',
    title: 'We read your Spotfire library and analysis files',
    intro: 'Instead of opening analyses one by one, we pull metadata straight from the Spotfire library and .dxp files. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Analyses, folders, and library items', from: 'Spotfire Server library APIs', use: 'Build the full inventory and group content by folder and business area' },
      { what: 'Data tables, visualizations, and calculated columns', from: 'Analysis files (.dxp)', use: 'Rebuild visuals, convert expressions to calculated fields, and spot duplicates' },
      { what: 'Information links and data sources', from: 'Information Designer library items', use: 'Map to Quick datasets and size the data landing in Amazon S3 or Redshift' },
      { what: 'Data functions and IronPython scripts', from: 'Analysis files and library', use: 'Move Python and R logic to AWS Lambda or SageMaker and flag scripts to redesign' },
      { what: 'Marking, filtering, and document properties', from: 'Analysis files (.dxp)', use: 'Recreate interactions as Quick filters, parameters, and actions' },
      { what: 'Users, groups, and library permissions', from: 'Spotfire Server administration', use: 'Recreate row-level security and map to SSO and IAM groups' },
      { what: 'Analysis opens and scheduled updates', from: 'Action logs and scheduled updates', use: 'Find the most-used and unused analyses to rank waves and plan refresh schedules' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every visualization traced back through its data tables and information links to the source.' },
        { title: 'Complexity score per analysis', description: 'Data functions, scripts, and custom expressions scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by the action logs, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Spotfire and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Spotfire admin sets up a read-only service account for the library and action logs. Nothing in Spotfire changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Spotfire migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every analysis.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled from the Spotfire library and .dxp files: data tables, information links, data functions, scripts, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Analyses grouped into waves by usage, shared information links, and folder. Unused analyses are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Information links mapped to datasets, data functions moved to Lambda or SageMaker, and visualizations rebuilt.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated analysis compared with its Spotfire original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Spotfire analyses today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Spotfire analyses used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
